import { post } from '../../networking';
import dataDogLogger from '../datadog';
import { v4 } from 'uuid';
import { COOKIE_KEYS, normalizeEmail, normalizePhone, sha256Hash } from './utils';

const ENVIRONMENT = process.env.NEXT_PUBLIC_NENV;
const trackingEnvironments = ['production'];

export const REDDIT_PIXEL_ID = 'a2_e24zajcj66vi';

export enum RedditClientSideEvent {
    PAGE_VISIT = 'PageVisit',
    ADD_TO_CART = 'AddToCart',
    SIGN_UP = 'SignUp',
    CUSTOM = 'Custom',
    PURCHASE = 'Purchase',
    VIEW_CONTENT = 'ViewContent'
}

export enum RedditServerSideEvent {
    PAGE_VISIT = 'PAGE_VISIT',
    ADD_TO_CART = 'ADD_TO_CART',
    SIGN_UP = 'SIGN_UP',
    CUSTOM = 'CUSTOM',
    PURCHASE = 'PURCHASE',
    VIEW_CONTENT = 'VIEW_CONTENT'
}

type RedditClientSideEventProperties = {
    conversionId?: string,
    itemCount?: number,
    value?: number,
    currency?: string,
    products?: {
        id?: string,
        category?: string,
        name?: string,
        quantity?: number,
        itemPrice?: number
    }[]
}

type RedditClientSideUserData = {
    email?: string,
    phoneNumber?: string,
    externalId?: string
}

type RedditClientSidePayload = RedditClientSideEventProperties & RedditClientSideUserData

type RedditServerSideEventProperties = {
    conversion_id: string,
    currency?: string,
    item_count?: number,
    value?: number,
    products?: {
        category?: string,
        id?: string,
        name?: string,
        quantity?: number,
        item_price?: number
    }[]
}

type RedditServerSideUserData = {
    email?: string,
    phone_number?: string,
    external_id?: string,
    user_agent?: string,
    uuid?: string
}

type RedditServerSidePayload = {
    data: {
        events: {
            click_id?: string,
            event_at: number,
            action_source: string,
            event_source_url: string,
            type: {
                custom_event_name?: string,
                tracking_type: RedditServerSideEvent
            },
            metadata: RedditServerSideEventProperties
            user: RedditServerSideUserData
        }[]
    }
}

const redditClientSideProxy = (eventName: RedditClientSideEvent, properties: RedditClientSidePayload) => {
    const rdt = window && window['rdt'];
    if (typeof rdt !== 'function') {
        dataDogLogger('error', `Reddit Pixel not initialized when firing event ${eventName}`);
        return;
    }

    if(!trackingEnvironments.includes(ENVIRONMENT)) {
        console.log('Reddit Pixel', eventName, properties);
        return;
    }

    rdt('track', eventName, properties);
};

const redditServerSideProxy = async (eventPayload: RedditServerSidePayload) => {
    if(!trackingEnvironments.includes(ENVIRONMENT)) {
        console.log('Reddit CAPI', eventPayload.data.events[0].type.tracking_type, eventPayload);
        return;
    }

    await post('tracking/reddit', eventPayload);
};

const mapClientEventNameToServerEventName = (event_name: RedditClientSideEvent) => {
    switch(event_name) {
    case RedditClientSideEvent.ADD_TO_CART:
        return RedditServerSideEvent.ADD_TO_CART;
    case RedditClientSideEvent.SIGN_UP:
        return RedditServerSideEvent.SIGN_UP;
    case RedditClientSideEvent.CUSTOM:
        return RedditServerSideEvent.CUSTOM;
    case RedditClientSideEvent.PURCHASE:
        return RedditServerSideEvent.PURCHASE;
    case RedditClientSideEvent.VIEW_CONTENT:
        return RedditServerSideEvent.VIEW_CONTENT;
    default:
        throw new Error(`Unknown event name: ${event_name}`);
    }
};

export default async ({
    event_name,
    customName,
    user,
    url,
    cookies,
    properties = {}
}: {
    event_name: RedditClientSideEvent,
    customName?: string,
    user?: any,
    url?: string,
    cookies?: any,
    properties?: RedditClientSideEventProperties
}) => {
    const event_id = v4();

    const clientSidePayload = {
        conversionId: event_id,
        ...(user?.id ? { externalId: sha256Hash(user?.id) } : {}),
        ...(user?.email ? { email: sha256Hash(normalizeEmail(user?.email)) } : {}),
        ...(user?.phone ? { phoneNumber: sha256Hash(normalizePhone(user?.phone)) } : {}),
        ...(customName ? { customEventName: customName } : {}),
        ...properties
    };
    redditClientSideProxy(event_name, clientSidePayload);

    const serverEventName = mapClientEventNameToServerEventName(event_name);

    const serverSidePayload = {
        data: {
            events: [{
                ...(cookies[COOKIE_KEYS.REDDIT_CLICK_ID] ? { click_id: cookies[COOKIE_KEYS.REDDIT_CLICK_ID] } : {}),
                event_at: Math.floor(Date.now()),
                action_source: 'WEBSITE',
                event_source_url: url,
                type: {
                    ...(event_name === RedditClientSideEvent.CUSTOM ? { custom_event_name: customName } : {}),
                    tracking_type: serverEventName
                },
                metadata: {
                    conversion_id: event_id,
                    ...(properties.itemCount ? { item_count: properties.itemCount } : {}),
                    ...(properties.value ? { value: properties.value } : {}),
                    ...(properties.currency ? { currency: properties.currency } : {}),
                    ...(properties.products ? { products: properties.products.map(product => ({
                        category: product.category,
                        id: product.id,
                        name: product.name,
                        quantity: product.quantity,
                        item_price: product.itemPrice
                    }))} : {})
                },
                user: {
                    ...(user?.email ? { email: sha256Hash(normalizeEmail(user?.email)) } : {}),
                    ...(user?.id ? { external_id: sha256Hash(user?.id) } : {}),
                    ...(user?.phone ? { phone_number: sha256Hash(normalizePhone(user?.phone)) } : {}),
                    ...(window.navigator.userAgent ? { user_agent: window.navigator.userAgent } : {}),
                    ...(cookies[COOKIE_KEYS['REDDIT_COOKIE1']] ? { uuid: cookies[COOKIE_KEYS['REDDIT_COOKIE1']] } : {})
                }
            }]
        }
    };

    redditServerSideProxy(serverSidePayload);
};
